import type { SlumbrNotification } from "@slumbr/core";
import { getVapidPublicKey, subscribeToPush, unsubscribeFromPush } from "@slumbr/core";

const STORAGE_KEY = "slumbr-notif-permission";
const ENDPOINT_KEY = "slumbr-push-endpoint";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function registerPushSubscription(): Promise<boolean> {
  if (!supportsWebNotifications()) return false;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  if (Notification.permission !== "granted") return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration.pushManager) return false;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const { publicKey } = await getVapidPublicKey();
      const keyBytes = urlBase64ToUint8Array(publicKey);
      // Hand the push manager a fresh ArrayBuffer so TS's BufferSource overload
      // accepts it on every platform (the Uint8Array variant has subtle
      // SharedArrayBuffer-vs-ArrayBuffer typing differences in lib.dom.d.ts).
      const buffer = keyBytes.buffer.slice(0) as ArrayBuffer;
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: buffer,
      });
    }
    const json = subscription.toJSON();
    const p256dh = json.keys?.p256dh ?? arrayBufferToBase64Url(subscription.getKey("p256dh"));
    const auth = json.keys?.auth ?? arrayBufferToBase64Url(subscription.getKey("auth"));
    if (!p256dh || !auth) return false;
    await subscribeToPush({
      endpoint: subscription.endpoint,
      keys: { p256dh, auth },
      userAgent: navigator.userAgent.slice(0, 256),
    });
    localStorage.setItem(ENDPOINT_KEY, subscription.endpoint);
    return true;
  } catch (error) {
    if (import.meta.env.DEV) console.warn("[push] registerPushSubscription failed:", error);
    return false;
  }
}

export async function unregisterPushSubscription(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager?.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe().catch(() => {});
      await unsubscribeFromPush(endpoint).catch(() => {});
    }
    localStorage.removeItem(ENDPOINT_KEY);
  } catch (error) {
    if (import.meta.env.DEV) console.warn("[push] unregisterPushSubscription failed:", error);
  }
}

export function supportsWebNotifications(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export function notificationPermission(): NotificationPermission | "unsupported" {
  if (!supportsWebNotifications()) return "unsupported";
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!supportsWebNotifications()) return "denied";
  if (Notification.permission === "denied") return "denied";
  let result: NotificationPermission;
  if (Notification.permission === "granted") {
    result = "granted";
  } else {
    try {
      result = await Notification.requestPermission();
    } catch {
      return "denied";
    }
    localStorage.setItem(STORAGE_KEY, result);
  }
  if (result === "granted") {
    // Register the push subscription with the backend so the SW receives push
    // events even when the page is closed. Failure here is non-fatal — local
    // foreground notifications still work via the Notification constructor.
    void registerPushSubscription().catch(() => {});
  }
  return result;
}

interface ShowOptions {
  title: string;
  body?: string;
  icon?: string;
  tag?: string;
  link?: string;
  silent?: boolean;
  renotify?: boolean;
  data?: Record<string, unknown>;
}

type SlumbrNotificationOptions = NotificationOptions & {
  renotify?: boolean;
};

export async function showWebNotification(options: ShowOptions): Promise<void> {
  if (!supportsWebNotifications()) return;
  if (Notification.permission !== "granted") return;
  // Don't double-fire if the tab is focused
  if (typeof document !== "undefined" && document.visibilityState === "visible" && document.hasFocus()) {
    return;
  }
  try {
    const init: SlumbrNotificationOptions = {
      ...(options.body ? { body: options.body } : {}),
      icon: options.icon ?? "/icons/icon-192.png",
      badge: "/icons/icon-192.png",
      tag: options.tag,
      renotify: options.renotify,
      silent: options.silent,
      data: { link: options.link, ...(options.data ?? {}) },
    };
    // Prefer service worker route on mobile; only the SW can show notifications when the
    // page is fully closed, and on iOS PWA installs `new Notification(...)` is unavailable.
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if (registration && typeof registration.showNotification === "function") {
        await registration.showNotification(options.title, init);
        return;
      }
    }
    const notification = new Notification(options.title, init);
    notification.onclick = () => {
      window.focus();
      if (options.link) {
        // SPA navigation: push the path into history and synthesize a
        // popstate so React Router renders the new route. Avoids the full
        // page reload `window.location.href = ...` would trigger, which
        // otherwise drops zustand state + interrupts WebRTC calls / Y.Doc
        // sessions in flight.
        try {
          window.history.pushState({}, "", options.link);
          window.dispatchEvent(new PopStateEvent("popstate"));
        } catch {
          window.location.href = options.link;
        }
      }
      notification.close();
    };
  } catch (error) {
    if (import.meta.env.DEV) console.warn("[notifications] showNotification failed:", error);
  }
}

export function notificationToWebOptions(notification: SlumbrNotification): ShowOptions {
  const metadata = notification.metadata ?? {};
  const actor = (metadata.actor ?? {}) as Record<string, unknown>;
  const avatarUrl = typeof actor.avatarUrl === "string" ? actor.avatarUrl : undefined;
  const icon = avatarUrl ?? "/icons/icon-192.png";
  return {
    title: notification.title,
    ...(notification.body ? { body: notification.body } : {}),
    icon,
    tag: notification.id,
    ...(notification.link ? { link: notification.link } : {}),
  };
}
